import cx from "classnames";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import React, { useCallback, useMemo } from "react";

import { DialogRefill } from "@/components/DialogRefill";
import { Button } from "@/components/elements/Button";
import { Media } from "@/components/elements/Media";
import { Text, TypeSizes } from "@/components/elements/Text";
import { NumberStepper } from "@/components/NumberStepper";
import { CartItem } from "@/hooks/useCart";
import useDialog from "@/hooks/useDialog";
import { useInventory } from "@/hooks/useInventory";
import useLocaleHelper from "@/hooks/useLanguageHelper";
import { useProducts } from "@/hooks/useProducts";
import { formatContainerSize, formatCurrency } from "@/utils/format";
import { getTimeAgoLength } from "@/utils/helpers";

import styles from "./styles.module.css";

export type CartLineItemProps = {
  item: CartItem;
  productTitleSize?: TypeSizes;
  className?: string;
  onChange: (value: number) => void;
  onRemove: () => void;
} & (
  | {
      version: "editable" | "summary" | "summaryCheckout";
      dateRefilled?: never;
      quantityRefilled?: never;
    }
  | {
      version: "homeStockCart" | "homeStockAccount";
      dateRefilled?: string;
      quantityRefilled: number;
    }
);

export const CartLineItem = ({
  item,
  productTitleSize = "s",
  version = "editable",
  dateRefilled,
  quantityRefilled,
  className,
  onChange,
  onRemove,
}: CartLineItemProps) => {
  const { locale } = useRouter();
  const { currentLanguage } = useLocaleHelper();
  const { t } = useTranslation();
  const isSummaryVersion =
    version === "summary" || version === "summaryCheckout";
  // Get product
  const { data: productsData } = useProducts();
  const { data: inventoryData } = useInventory();
  const productsById = productsData?.productsById || {};
  const productQuantitiesBySku = inventoryData?.productQuantitiesBySku || {};
  const { id, quantity, price } = item;
  const { title, brand, size, sku_code, product_image } =
    productsById[id] || {};
  const availableQuantity =
    productQuantitiesBySku[sku_code]?.available_quantity;
  // Handle home stock summary
  const refilledTimeAgo = useMemo(() => {
    return (
      dateRefilled &&
      getTimeAgoLength({
        date: dateRefilled,
        locale: currentLanguage,
      })
    );
  }, [dateRefilled, locale]);
  // Handle starting refill
  // START RETURN FLOW
  const { showDialog } = useDialog();
  const showRefillDialog = useCallback(() => {
    const content = (
      <DialogRefill
        item={item}
        dateRefilled={dateRefilled}
        quantityRefilled={quantityRefilled}
      />
    );
    showDialog(content, t("order_overview_add_title"));
  }, [item, showDialog]);

  // Handle missing products
  if (!id) return null;

  return (
    <li className={cx(styles.item, styles[version], className)}>
      <div className={styles.inner}>
        <div className={styles.figureContainer}>
          <Media
            src={product_image}
            alt={t(title)}
            aspectRatio="square"
            objectFit="cover"
          />
        </div>
        <div className={styles.info}>
          <div className={styles.info_topLine}>
            <div>
              <Text
                as="div"
                size={productTitleSize}
                className={styles.info_product}
              >
                {t(title)}
              </Text>
              <Text
                as="div"
                size={version === "summaryCheckout" ? "xxs" : "xs"}
                className={styles.info_brand}
                disableMarginBottom
              >
                {brand}
              </Text>
            </div>
            {version === "homeStockAccount" || version === "homeStockCart" ? (
              <Button
                size="small"
                onClick={showRefillDialog}
                disabled={!availableQuantity || availableQuantity <= 0} // catch negative values
              >
                {t("refill_CTA")}
              </Button>
            ) : (
              <div>
                <Text
                  as="div"
                  size={productTitleSize}
                  className={styles.info_price}
                >
                  {formatCurrency(price)}
                </Text>

                {!isSummaryVersion && (
                  <Text as="div" size="xxs" className={styles.containerSize}>
                    {formatContainerSize(size)}
                  </Text>
                )}
              </div>
            )}
          </div>
          {version !== "homeStockCart" && (
            <div className={styles.info_bottomLine}>
              {version === "editable" ? (
                <>
                  <NumberStepper
                    name={t(title)}
                    minValue={0}
                    maxValue={availableQuantity}
                    value={quantity}
                    onChange={onChange}
                    label={`${t(title)} ${t("order_detail_quantity")}`}
                    hideLabel
                  />
                  <Button
                    onClick={onRemove}
                    className={styles.removeButton}
                    theme="underline"
                    size="x-small"
                  >
                    {t("shopping_cart_remove")}
                  </Button>
                </>
              ) : version === "homeStockAccount" ? (
                !!dateRefilled && (
                  <Text
                    size="xxs"
                    responsive
                    className={styles.homeStockSummaryButton}
                  >
                    {t("overview_storage_refilled_status", {
                      timeAgo: refilledTimeAgo,
                    })}
                  </Text>
                )
              ) : (
                <Text size="xxs" className={styles.summaryInfo}>
                  {t("order_detail_quantity")} {quantity} ×{" "}
                  {formatContainerSize(size)}
                </Text>
              )}
            </div>
          )}
        </div>
      </div>
    </li>
  );
};

export default CartLineItem;
